import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraPersonPicker } from '@mono/ui-components/captains';
import { SpecraButton, SpecraEyebrow, SpecraHeading, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraPopover } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { projectDataClient } from '../../../../lib/connect/clients';
function shortDate(iso) {
    const d = new Date(`${iso}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC'
    });
}
function OwnerField({ canEdit, onChange, people, userId }) {
    const [anchor, setAnchor] = useState(null);
    const [open, setOpen] = useState(false);
    const person = people.find((p)=>p.userId === userId);
    const text = userId ? `Owner ${person?.name || person?.email || 'Unknown user'}` : 'No owner';
    if (!canEdit) {
        return /*#__PURE__*/ _jsx(SpecraText, {
            size: "meta",
            tone: userId ? 'muted' : 'danger',
            children: text
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                className: userId ? 'text-muted-foreground' : 'text-danger',
                onClick: ()=>setOpen((o)=>!o),
                ref: setAnchor,
                size: "inline",
                variant: "link",
                children: text
            }),
            /*#__PURE__*/ _jsx(SpecraPopover, {
                anchor: anchor,
                "aria-label": "Change owner",
                onOpenChange: setOpen,
                open: open,
                children: /*#__PURE__*/ _jsx("div", {
                    className: "w-72",
                    children: /*#__PURE__*/ _jsx(SpecraPersonPicker, {
                        label: "Owner",
                        onPersonChange: (p)=>{
                            if (p) onChange(p.id);
                            setOpen(false);
                        },
                        people: people.map((p)=>({
                                id: p.userId,
                                name: p.name || p.email,
                                email: p.email
                            })),
                        placeholder: "Choose a person",
                        value: userId || undefined
                    })
                })
            })
        ]
    });
}
/**
 * The proposal section head (WRT-17): "Volume · §n", the owner (editable),
 * who it is due to, what it answers, and the section title. With no section
 * selected it shows the volume and how many sections are ready.
 */ export function ProposalSectionHead({ canEdit, n, nextReviewName, people, projectId, readyCount, section, sectionCount, volumeName }) {
    const qc = useQueryClient();
    const assign = useMutation({
        mutationFn: (writerUserId)=>projectDataClient.updateOutlineSectionAssignments({
                projectId,
                sectionId: section?.sectionId ?? '',
                writerUserId
            }),
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: [
                    'outline-sections',
                    projectId
                ]
            })
    });
    if (!section) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-[var(--space-1)]",
            "data-testid": Testids.officeWorkspace.proposalSectionHead,
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    level: 1,
                    children: volumeName
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: sectionCount === 0 ? 'No sections yet. Headings appear here after the first save.' : `${sectionCount} ${sectionCount === 1 ? 'section' : 'sections'} · ${readyCount} marked ready`
                })
            ]
        });
    }
    const refs = section.requirementIds.length;
    const meta = [
        section.internalDate ? `due ${shortDate(section.internalDate)}${nextReviewName ? ` for ${nextReviewName} team` : ''}` : '',
        refs > 0 ? `answers ${refs} ${refs === 1 ? 'requirement' : 'requirements'}` : ''
    ].filter(Boolean);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-1)]",
        "data-testid": Testids.officeWorkspace.proposalSectionHead,
        children: [
            /*#__PURE__*/ _jsx(SpecraEyebrow, {
                children: `${volumeName} · §${n}`
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-x-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(OwnerField, {
                        canEdit: canEdit,
                        onChange: (id)=>assign.mutate(id),
                        people: people,
                        userId: section.writerUserId ?? ''
                    }),
                    meta.length > 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: `· ${meta.join(' · ')}`
                    }) : null,
                    assign.isError ? /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "danger",
                        children: "The owner could not be changed. Try again."
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraHeading, {
                level: 1,
                children: section.title
            })
        ]
    });
}
